import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../../../utils/apiCaller';

/**
 * Destination route for the impersonation handoff. Runs at
 *   /impersonation/landing?otx=<64-char hex>
 *
 * Reads the OTX from the query string, POSTs /api/impersonation/exchange
 * to get the real bearer, stores it in sessionStorage (tab-isolated —
 * the god-user's other tabs keep their own mikaBearerToken untouched),
 * then redirects to /dashboard where the tenant app boots and the
 * ImpersonationBanner mounts.
 *
 * On any error (expired OTX, target deleted, company suspended mid-flight)
 * we show a plain screen with a link back to Mika Ops rather than trying
 * to recover.
 */
interface ExchangeResponse {
    access_token: string;
    session: {
        id: number;
        started_at: string;
        expires_at: string;
    };
    target: {
        user_id: number;
        user_email: string | null;
        user_fullname: string;
        company_id: number;
        company_name: string;
    };
}

const ImpersonationLandingPage: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    // React 18 StrictMode + dev server effects-run-twice makes an OTX
    // accidentally single-use itself. Guard against that with a ref the
    // second invocation sees.
    const exchangedRef = useRef(false);

    useEffect(() => {
        if (exchangedRef.current) return;
        exchangedRef.current = true;

        const params = new URLSearchParams(window.location.search);
        const otx = params.get('otx');
        if (!otx || otx.length !== 64) {
            setError('This impersonation link is invalid.');
            return;
        }

        (async () => {
            try {
                const res = await axios.post<ExchangeResponse>(
                    apiUrl + 'impersonation/exchange',
                    { otx },
                    { headers: { Accept: 'application/json' } },
                );

                sessionStorage.setItem('imp_access_token', res.data.access_token);
                sessionStorage.setItem(
                    'imp_session_meta',
                    JSON.stringify({
                        session_id: res.data.session.id,
                        started_at: res.data.session.started_at,
                        expires_at: res.data.session.expires_at,
                        target_user_email: res.data.target.user_email,
                        target_user_fullname: res.data.target.user_fullname,
                        target_company_id: res.data.target.company_id,
                        target_company_name: res.data.target.company_name,
                    }),
                );

                // Pick the landing module the same way Login.tsx does: call
                // /users/permissions-responsibilities, take the first permission
                // key's module segment, route there. The response envelope is
                // axios.data -> { data: { permissions: {...} } } (the service
                // wraps its own 'data' key). Getting the depth wrong silently
                // sends every target to the /admin fallback → contact redirect.
                let dest: string | null = null;
                try {
                    const permRes = await axios.get(
                        apiUrl + 'users/permissions-responsibilities',
                        {
                            headers: {
                                Authorization: `Bearer ${res.data.access_token}`,
                                Accept: 'application/json',
                            },
                        },
                    );
                    const permissions = permRes.data?.data?.permissions ?? {};
                    if (permissions.superAdmin) {
                        dest = '/dashboard/analytics';
                    } else if (permissions.fullPrivileges) {
                        dest = '/dashboard/contact';
                    } else {
                        const firstKey = Object.keys(permissions)[0];
                        if (firstKey) {
                            const moduleName = firstKey.split('.')[0];
                            if (moduleName === 'production') {
                                dest = '/dashboard/production/board';
                            } else {
                                dest = `/dashboard/${moduleName}`;
                            }
                        }
                    }
                } catch {
                    // Permissions fetch failed. Fall through to the error branch.
                }

                if (dest === null) {
                    setError('The target user has no accessible modules, so there is no page to land on.');
                    return;
                }

                // Strip the OTX out of history before the redirect so the back
                // button can't retry a consumed OTX.
                window.history.replaceState({}, '', dest);
                window.location.replace(dest);
            } catch (err: any) {
                const message = err?.response?.data?.message;
                const code = err?.response?.data?.code;
                if (code === 'impersonation.otx_invalid') {
                    setError('This impersonation link has expired or already been used.');
                } else if (code === 'impersonation.target_deleted') {
                    setError('The target user was removed. Return to Mika Ops to pick another target.');
                } else if (code === 'impersonation.company_not_active') {
                    setError('The target company has been suspended or marked inactive.');
                } else {
                    setError(message ?? 'We could not open this impersonation session.');
                }
            }
        })();
    }, []);

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#0f0f1a',
                color: '#edecf5',
                fontFamily: 'Inter, system-ui, sans-serif',
                padding: 24,
            }}
        >
            <div
                style={{
                    maxWidth: 480,
                    width: '100%',
                    background: '#1a1a32',
                    border: '1px solid #2a2a4a',
                    borderRadius: 10,
                    padding: 32,
                    textAlign: 'center',
                }}
            >
                {error === null ? (
                    <>
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                border: '3px solid #f59e0b',
                                borderRightColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'imp-spin 0.7s linear infinite',
                                margin: '0 auto 20px',
                            }}
                        />
                        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                            Opening impersonation session…
                        </div>
                        <div style={{ fontSize: 13, color: '#908aa8' }}>
                            You'll land in the tenant app in a moment.
                        </div>
                        <style>{`@keyframes imp-spin { to { transform: rotate(360deg); } }`}</style>
                    </>
                ) : (
                    <>
                        <div
                            style={{
                                fontSize: 15,
                                fontWeight: 600,
                                marginBottom: 10,
                                color: '#fb7185',
                            }}
                        >
                            Impersonation failed
                        </div>
                        <div style={{ fontSize: 13, color: '#c8c4da', marginBottom: 20 }}>
                            {error}
                        </div>
                        <a
                            href="/mika/ops/companies"
                            style={{
                                display: 'inline-block',
                                padding: '8px 16px',
                                background: 'transparent',
                                color: '#f5f5f5',
                                border: '1px solid #2a2a4a',
                                borderRadius: 6,
                                fontSize: 13,
                                textDecoration: 'none',
                            }}
                        >
                            Back to Mika Ops
                        </a>
                    </>
                )}
            </div>
        </div>
    );
};

export default ImpersonationLandingPage;
